import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { ConfirmDialog } from "../../../shared/ui";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { MarkdownContent } from "../../../components/chat/MarkdownContent";
import { CitedMarkdown } from "../../../components/widgets/narrative/CitedMarkdown";
import { ClarificationPanel } from "./ClarificationPanel";
import { IncidentSpecificsPanel } from "./IncidentSpecificsPanel";
import { ReportModeDialog, type ReportMode } from "./ReportModeDialog";
import type {
  Citation,
  CitationGroup,
} from "../../../features/narrative-citations/types";
import type { NormalizedPlanPresentation, ReviewEntity, ReviewGap } from "../types";

type PlanTab = "first-draft" | "specifics";

interface PlanApprovalCardProps {
  plan: NormalizedPlanPresentation;
  feedback: string;
  onFeedbackChange: (value: string) => void;
  /** Resolves false when the approval was rejected and nothing was submitted
   *  (e.g. the server's 502 on a full report) — the officer stays put. */
  onApprove: (options: { fullReport: boolean }) => void | Promise<boolean | void>;
  onRequestChanges: () => void | Promise<void>;
  isSubmitting: boolean;
  isAwaitingRevision: boolean;
  entities?: ReviewEntity[];
  gaps: ReviewGap[];
  onResolveGap: (id: string, resolution: string) => void;
  onDismissGap: (id: string) => void;
  onRestoreGap: (id: string) => void;
  /** Latest submission error, shown against the choice that failed. */
  submitError?: string;
  /** Wipes a previous approval failure before the mode dialog reopens. */
  onClearError?: () => void;
  citationsEnabled?: boolean;
  onCiteMarkdown?: (group: CitationGroup) => void;
  canCiteMarkdown?: (citation: Citation) => boolean;
}

export const PlanApprovalCard: React.FC<PlanApprovalCardProps> = ({
  plan,
  feedback,
  onFeedbackChange,
  onApprove,
  onRequestChanges,
  isSubmitting,
  isAwaitingRevision,
  entities = [],
  gaps,
  onResolveGap,
  onDismissGap,
  onRestoreGap,
  submitError,
  onClearError,
  citationsEnabled = false,
  onCiteMarkdown,
  canCiteMarkdown,
}) => {
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<PlanTab>("first-draft");
  const actionsDisabled = isSubmitting || isAwaitingRevision;
  const hasGaps = gaps.length > 0;
  const hasClarificationResponses = useMemo(
    () => gaps.some((gap) => !gap.dismissed && Boolean(gap.resolution?.trim())),
    [gaps]
  );
  // A dismissed question isn't unresolved — the officer answered it with
  // "doesn't apply", so it shouldn't trigger the unanswered-questions warning
  // on approval. Its dismissal still rides along in the resume payload.
  const hasUnresolvedGaps = useMemo(
    () => gaps.some((gap) => !gap.dismissed && !gap.resolution?.trim()),
    [gaps]
  );
  // "Has anything to send back" — gates the Request Changes button. Approve
  // is independent: it always carries whatever's typed/resolved alongside
  // { approved: true } via the hook's submitApproval composer.
  const hasOfficerInput =
    feedback.trim().length > 0 || hasClarificationResponses;

  // Approving with typed input is legal — it rides along with the approval —
  // but it silently skips the revision the officer may have been expecting, so
  // confirm first. Unresolved gaps get the same treatment.
  const approveConfirmDescription = hasOfficerInput
    ? hasUnresolvedGaps
      ? "All comments you've entered will be submitted along with your approval rather than sent back for a revision, and some of the AI Assistant's questions are still unanswered. Next you'll choose what gets generated."
      : "All comments you've entered will be submitted along with your approval rather than sent back for a revision. Next you'll choose what gets generated."
    : "Are you sure you want to approve this First Draft without clarifying the AI Assistant's questions? The narrative will be approved as is. Next you'll choose what gets generated.";

  // Approval is two steps: (1) confirm, only when there's unsent officer input
  // or an unanswered question, then (2) always pick what gets generated. The
  // mode choice is the last gate before the thread resumes, so it's the same
  // dialog whether or not the confirmation preceded it.
  const openModeDialog = () => {
    onClearError?.();
    setModeDialogOpen(true);
  };

  const handleApprove = () => {
    if (actionsDisabled) return;
    if (hasUnresolvedGaps || hasOfficerInput) {
      setConfirmApproveOpen(true);
      return;
    }
    openModeDialog();
  };

  // A rejected approval submitted nothing, so the choice is still open —
  // keep the dialog up with the server's message rather than dropping the
  // officer back onto the card with an alert scrolled off the top.
  const handleSelectMode = (mode: ReportMode) => {
    void Promise.resolve(onApprove({ fullReport: mode === "full" })).then(
      (accepted) => {
        if (accepted !== false) setModeDialogOpen(false);
      }
    );
  };

  const handleRequestChanges = () => {
    if (actionsDisabled) return;
    void onRequestChanges();
  };

  // Entities the officer removed stay out of the specifics view — the tabs
  // only exist when the planner extracted something to show.
  const visibleEntities = useMemo(
    () => entities.filter((entity) => !entity.removed),
    [entities]
  );
  const hasEntities = visibleEntities.length > 0;

  // A revised plan can arrive without entities; don't strand the officer on a
  // tab that no longer renders.
  useEffect(() => {
    if (!hasEntities) setActiveTab("first-draft");
  }, [hasEntities]);

  const firstDraftBody = (
    <Box
      role={hasEntities ? "tabpanel" : undefined}
      id={hasEntities ? `plan-tabpanel-${activeTab}` : undefined}
      aria-labelledby={hasEntities ? `plan-tab-${activeTab}` : undefined}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: { xs: 2, md: 2.5 },
        bgcolor: "background.paper",
      }}
    >
      {hasEntities && activeTab === "specifics" ? (
        <IncidentSpecificsPanel entities={visibleEntities} />
      ) : citationsEnabled ? (
        <CitedMarkdown
          content={plan.markdown}
          onCite={onCiteMarkdown}
          canCite={canCiteMarkdown}
        />
      ) : (
        <MarkdownContent content={plan.markdown} />
      )}
    </Box>
  );

  const firstDraft = hasEntities ? (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={activeTab}
        onChange={(_event, value: PlanTab) => setActiveTab(value)}
        aria-label="First Draft views"
        sx={{ minHeight: 40, mb: 1.5 }}
      >
        <Tab
          value="first-draft"
          id="plan-tab-first-draft"
          aria-controls="plan-tabpanel-first-draft"
          label="Narrative"
          sx={{ minHeight: 40 }}
        />
        <Tab
          value="specifics"
          id="plan-tab-specifics"
          aria-controls="plan-tabpanel-specifics"
          label={`Incident Specifics (${visibleEntities.length})`}
          sx={{ minHeight: 40 }}
        />
      </Tabs>
      {firstDraftBody}
    </Box>
  ) : (
    firstDraftBody
  );

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {plan.title}
            </Typography>
            {plan.summary && (
              <Typography variant="body2" color="text.secondary">
                {plan.summary}
              </Typography>
            )}
          </Box>

          {isAwaitingRevision && (
            <Alert severity="info">
              AI Assistant is revising the First Draft with your feedback.
            </Alert>
          )}

          {hasGaps ? (
            // First Draft and the AI Assistant's open questions sit side by side
            // so the officer can answer each question against the First Draft
            // without switching views. Stacks on narrow screens.
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 2, md: 3 },
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ flex: { md: 2 }, minWidth: 0, width: "100%" }}>
                {firstDraft}
              </Box>
              <Box sx={{ flex: { md: 1 }, minWidth: 0, width: "100%" }}>
                <ClarificationPanel
                  gaps={gaps}
                  onResolveGap={onResolveGap}
                  onDismissGap={onDismissGap}
                  onRestoreGap={onRestoreGap}
                  disabled={actionsDisabled}
                />
              </Box>
            </Box>
          ) : (
            firstDraft
          )}

          <TextField
            label="Request changes"
            placeholder="Explain what the AI Assistant should change before generation starts."
            value={feedback}
            onChange={(event) => onFeedbackChange(event.target.value)}
            multiline
            minRows={4}
            fullWidth
            disabled={isSubmitting}
          />

          {/* Two distinct actions, both always visible. The officer picks the
              path explicitly: Request Changes routes to the planner for
              revision; Approve sends officer comments and gap clarifications
              to the orchestrator and proceeds with extraction. */}
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<EditNoteIcon />}
              onClick={handleRequestChanges}
              disabled={actionsDisabled || !hasOfficerInput}
            >
              {isAwaitingRevision ? "Revising..." : "Request Changes"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleApprove}
              disabled={actionsDisabled}
            >
              Approve First Draft
            </Button>
          </Stack>
        </Stack>
      </CardContent>

      <ConfirmDialog
        open={confirmApproveOpen}
        onClose={() => setConfirmApproveOpen(false)}
        onConfirm={() => {
          setConfirmApproveOpen(false);
          openModeDialog();
        }}
        title="Approve First Draft?"
        description={approveConfirmDescription}
        tone="warning"
        confirmColor="primary"
        confirmLabel="Approve Anyway"
        cancelLabel="Keep Editing"
      />

      <ReportModeDialog
        open={modeDialogOpen}
        onClose={() => setModeDialogOpen(false)}
        onSelect={handleSelectMode}
        submitting={isSubmitting}
        error={submitError}
      />
    </Card>
  );
};
